import { useEffect, useMemo, useState } from "react";
import { DEFAULT_PAGINATION } from "../../../constants/pagination";
import {
  debounce,
  formatDateFieldsFlat,
  formatNumberFixed,
  renderClassNameStatusEsp,
} from "../../../utils/helper";
import { Button, Flex, Input, Space, Table, Tag } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faFilter,
  faPen,
  faSearch,
  faTimeline,
  faEye,
  faRotateRight,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { IColumnConfig, ISortInfo } from "../../../interfaces/common";
import CustomizeTableModal from "../../../components/customize-table";
import { ColumnType, TableProps } from "antd/es/table";
import FilterDrawer from "../../../components/filter-drawer";
import { TableViewFilters } from "../../../interfaces/table-view";
import TableViewDropdown from "../../../components/table-view-dropdown";
import { useTableViews } from "../../../hooks/useTableViews";
import dayjs from "dayjs";
import { DATE_FORMAT, DATE_FORMAT_DDMMYYYY_HHMMSS } from "../../../constants/common";
import { useUpdateTableView } from "../../../hooks/useTableView";
import { countNonEmptyFields } from "../../../utils/helper";
import { useNavigate } from "react-router-dom";
import { usePageStore } from "../../../context/PageStoreContext";
import { useMessageApi } from "../../../context/MessageContext";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { PERMISSIONS } from "../../../constants/permission";
import { WorkflowServices } from "../../../services/workflow";
import { APWorkflowStatusTab } from "../../../constants/ap-workflow";
import {
  IApWorkflowResult,
  ITourEventOrOtherWorkflowResponse,
  ITourEventOrOtherWorkflowResult,
} from "../../../interfaces/ap-workflow";
import { ApWorkflowService } from "../../../services/ap-workflow";
import ModalCreateBatch from "./create-batch";
import { IWorkflowType } from "../../../interfaces/workflow";
import { ERROR_CODES, WorkflowTypeEnum } from "../../../constants/enum";
import { useMasterData } from "../../../hooks/useMasterData";
import { MASTERDATA_TYPE } from "../../../constants/master-data";
import FilterTourEventOrOtherWorkflow from "./filter-tour-event-or-other";
import ModalConfirm from "../../../components/confirm";
import { useViewFile } from "../../../hooks/useViewFile";

const COL_FIXED_LEFT = ["espSupplierCode"];

const renderFieldTable = (fieldName: string, value: any) => {
  const dateFields = ["espDepartureDate", "espDueDate"];
  const numberFields = ["espAmount"];

  if (dateFields.includes(fieldName)) {
    return value ? dayjs(value).format(DATE_FORMAT) : value;
  }

  if (numberFields.includes(fieldName)) {
    return <div style={{ textAlign: "right" }}>{value ? formatNumberFixed(value) : value}</div>;
  }

  if (fieldName === "flag") {
    return value && value.map((item: string) => <Tag key={item}>{item}</Tag>);
  }

  if (fieldName === "status") {
    return <span className={renderClassNameStatusEsp(value)}>{value}</span>;
  }

  return value;
};

const setWidthFieldTable = (fieldName: string) => {
  if (["espBookingNo"].includes(fieldName)) {
    return 180;
  }
  if (["espCurrency", "team"].includes(fieldName)) {
    return 110;
  }

  return 150;
};

interface TourEventOrOtherWorkflowTableProps extends IWorkflowType {
  tabKey: string;
}

const TourEventOrOtherWorkflowTable: React.FC<TourEventOrOtherWorkflowTableProps> = ({ tabKey, type }) => {
  const navigate = useNavigate();
  const hasPermissionEditor = useHasPermission([PERMISSIONS.WORKFLOW_EDITOR]);
  const messageApi = useMessageApi();
  const [listData, setListData] = useState<ITourEventOrOtherWorkflowResult[]>([]);
  const [currentStatus, setCurrentStatus] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);
  const [sortInfo, setSortInfo] = useState<ISortInfo>({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGINATION.pageSize,
    total: 0,
  });
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");

  const [showModal, setShowModal] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [isReload, setIsReload] = useState(false);
  const [modalConfirm, setModalConfirm] = useState<any>(null);
  const [showModalCreateBatch, setShowModalCreateBatch] = useState<boolean>(false);
  const [changeStatusLoading, setChangeStatusLoading] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const { setPageValue, getPageValue } = usePageStore();
  const [childSorters, setChildSorters] = useState<Record<string, any>>({});
  const { onDownloadBatchExport } = useViewFile();
  const [isFetchedMaterData, setIsFetchedMaterData] = useState(false);
  

  const TABLE_NAME = type === WorkflowTypeEnum.TourEvent ? `tour_workflow_${tabKey}` : `other_workflow_${tabKey}`;

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedRowKeys([]);
    setListData([]);
    setCurrentStatus(tabKey);
  }, [tabKey, type]);

  useEffect(() => {
    setSearchValue(getPageValue().searchValue);
    setDebouncedSearchValue(getPageValue().searchValue);
    setPagination((prev) => ({ ...prev, ...getPageValue().pagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  if (TABLE_NAME) {
    refetchTableViews();
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [TABLE_NAME]);

  const {
    dataTableView,
    setDataTableView,
    loading: loadingTableView,
    refetchTableViews,
  } = useTableViews({
    tableName: TABLE_NAME,
    shouldFetch: false
  });

  const currentTableView = dataTableView?.currentView!;

  const { filters = {}, columns: columnConfigs = [] } = currentTableView || {};

  const isDefaultView = !!dataTableView?.defaultView;

  const { updateTableView } = useUpdateTableView();

  const activeFilterCount = countNonEmptyFields(filters);

  const typesMasterData = useMemo(() => [MASTERDATA_TYPE.CURRENCY], []);
  const { data: masterData } = useMasterData({
    types: typesMasterData,
    includeUsers: false,
    enabled: isFetchedMaterData
  });

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = {
        keyword: debouncedSearchValue || undefined,
        ...formatDateFieldsFlat(filters),
        tab: currentStatus,
        type: type,
        page: pagination.current,
        pageSize: pagination.pageSize,
        orderBy: sortInfo.orderBy,
        orderDirection: sortInfo.orderDirection,
      };
      const response: ITourEventOrOtherWorkflowResponse = await WorkflowServices.getAllEspWorkflow(params);
      setListData(response.data.results);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.totalElements || 0,
      }));
    } catch (error) {
      console.error("Fetch suppliers error:", error);
    } finally {
      setLoading(false);
      setIsFetchedMaterData(true);
    }
  };

  useEffect(() => {
    if (!loadingTableView) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadingTableView,
    pagination.current,
    pagination.pageSize,
    sortInfo,
    debouncedSearchValue,
    filters,
    currentStatus,
    type,
    isReload,
  ]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearchValue(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
      }, 1000),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleSaveCustomizeTable = async (configs: IColumnConfig[]) => {
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, columns: configs } });
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination((prev) => ({
      ...prev,
      current: paginationConfig.pageSize !== prev.pageSize ? 1 : paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }));

    // Handle sorting
    if (sorter?.field) {
      setSortInfo({
        orderBy: sorter.field,
        orderDirection: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      // Clear sorting
      setSortInfo({});
    }
  };

  const handleApplyFilters = (values: TableViewFilters) => {
    const newFilters = { ...filters, ...values };
    setDataTableView({ ...dataTableView!, currentView: { ...currentTableView, filters: newFilters } });
    if (isDefaultView) {
      updateTableView({
        tableName: TABLE_NAME,
        filters: newFilters,
      });
    }
    setDebouncedSearchValue("");
    setSearchValue("");
    setOpenFilter(false);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleReload = async () => {
    setSearchValue("");
    setDebouncedSearchValue("");
    setPagination((prev) => ({ ...prev, current: 1 }));
    setIsReload(!isReload);
  };

  const columns: ColumnType<ITourEventOrOtherWorkflowResult>[] = useMemo(() => {
    return columnConfigs.map((col) => {
      return {
        title: col.label,
        dataIndex: col.column,
        key: col.column,
        fixed: COL_FIXED_LEFT.includes(col.column) ? "left" : false,
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (value: any) => renderFieldTable(col.column, value),
        ...(setWidthFieldTable(col.column) && { width: setWidthFieldTable(col.column) }),
      };
    });
  }, [columnConfigs]);

  const colActive: ColumnType<ITourEventOrOtherWorkflowResult> = {
    title: "Actions",
    key: "action",
    width: 120,
    fixed: "right",
    align: "center",
    render: (_: any, record: ITourEventOrOtherWorkflowResult) => {
      return (
        <Space size="small" className="box-action" align="center">
          <Button type="link" size="small" onClick={() => navigateDetail(record.id)}>
            <FontAwesomeIcon icon={hasPermissionEditor ? faPen : faEye} />
          </Button>
          <div>|</div>
          <Button type="link" size="small" onClick={() => linkToAudit(record.id)}>
            <FontAwesomeIcon icon={faTimeline} />
          </Button>
        </Space>
      );
    },
  };

  const batchColumns = [
    {
      title: "Batch Name",
      dataIndex: "batchName",
      key: "batchName",
    },
    {
      title: "Exported Datetime",
      dataIndex: "exportedDatetime",
      key: "exportedDatetime",
      render: (value: any) => (value ? dayjs(value).format(DATE_FORMAT_DDMMYYYY_HHMMSS) : ""),
    },
    {
      title: "ESP Matching Records Total",
      dataIndex: "espWorkflowRecordsTotal",
      key: "espWorkflowRecordsTotal",
      render: (value: any) => value ?? 0,
    },
    {
      title: "Action",
      key: "operation",
      render: (_: any, record: any) => (
        <Button
          type="link"
          icon={<FontAwesomeIcon icon={faDownload} />}
          onClick={(e) => {
            e.stopPropagation();
            onDownloadBatchExport?.(Number(record?.batchId), record?.batchName);
          }}
        />
      ),
    },
  ];

  const linkToAudit = (id: string) => {
    setPageValueTable();
    const linkName = type === WorkflowTypeEnum.TourEvent ? `tour-event-workflow` : `other-workflow`;
    navigate(`/${linkName}/${id}/audit-trail`);
  };

  const navigateDetail = (id: string) => {
    setPageValueTable();
    const linkName = type === WorkflowTypeEnum.TourEvent ? `tour-event-workflow` : `other-workflow`;
    navigate(`/${linkName}/${id}`);
  };

  const setPageValueTable = () => {
    setPageValue({
      searchValue,
      pagination,
      currentStatus,
    });
  };

  const rowSelection: TableProps<any>["rowSelection"] = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record: IApWorkflowResult) => ({
      id: record.id,
    }),
    preserveSelectedRowKeys: true,
  };

  const handleCreateBatch = async (name?: string, isForce?: boolean) => {
    try {
      const response = await ApWorkflowService.createBatch({ type, ids: selectedRowKeys, name, isForce });
      const { success } = response;
      if (success) {
        messageApi.success("Batch created successfully");
        setShowModalCreateBatch(false);
        setSelectedRowKeys([]);
        handleReload();
      }
    } catch (error: any) {
      if (error.errorCode === ERROR_CODES.SIR_FULL_ERROR) {
        setShowModalCreateBatch(false);
        setModalConfirm({
          isOpen: true,
          title: "Confirm Create Batch",
          description: `At least 1 invoice in this batch has SIRs that have not been selected, do you still want to proceed posting?`,
          onConfirm: () => handleCreateBatch(name, true),
        });
      } else {
        messageApi.error(error.message || "Create batch failed");
      }
    }
  };

  const handleChangeBatchStatus = async () => {
    try {
      setChangeStatusLoading(true);
      const response = await ApWorkflowService.changeBatchStatus(selectedRowKeys, type);
      const { success } = response;
      if (success) {
        messageApi.success("Batch is now completed");
        setShowModalCreateBatch(false);
        setSelectedRowKeys([]);
        handleReload();
      }
    } catch (error: any) {
      messageApi.error(error.message || "Change batch status failed");
    } finally {
      setChangeStatusLoading(false);
    }
  };

  const renderActionButtons = () => {
    if (!hasPermissionEditor) return null;
    if (selectedRowKeys.length === 0) return null;

    switch (tabKey) {
      case APWorkflowStatusTab.REVIEW:
        return (
          <Button type="primary" onClick={() => setShowModalCreateBatch(true)} style={{ marginLeft: 8 }}>
            {`Post to Finance for ${selectedRowKeys.length} selected items`}
          </Button>
        );

      case APWorkflowStatusTab.EXPORTED:
        return (
          <Button type="primary" onClick={() => handleChangeBatchStatus()} loading={changeStatusLoading}>
            {`Mark Completed for ${selectedRowKeys.length} selected items`}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Flex gap={16} justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Input
          value={searchValue}
          placeholder="Enter Booking Ref., Supp. Code, Supp. Name or Lead Pax Name"
          onChange={handleInputChange}
          prefix={<FontAwesomeIcon icon={faSearch} style={{ color: "var(--text-blue-500)" }} />}
          style={{ width: 600 }}
          size="large"
          allowClear
        />
        <Flex gap={12}>
          {renderActionButtons()}
          <Button color="primary" variant="outlined" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon={faBars} style={{ fontSize: "16px" }} />
            Customise
          </Button>
          <Button color="primary" variant="outlined" onClick={() => setOpenFilter(true)}>
            <FontAwesomeIcon icon={faFilter} style={{ fontSize: "16px" }} />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
          <TableViewDropdown
            tableName={TABLE_NAME}
            dataTableView={dataTableView!}
            onTrigger={() => {
              refetchTableViews();
            }}
          />
          <Button color="primary" variant="outlined" onClick={handleReload}>
            <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: "16px" }} />
            Reload
          </Button>
        </Flex>
      </Flex>
      <Table
        rowSelection={tabKey !== APWorkflowStatusTab.COMPLETED ? rowSelection : undefined}
        dataSource={listData}
        loading={loading || loadingTableView}
        rowKey={(record) => record.id}
        columns={tabKey !== APWorkflowStatusTab.EXPORTED ? [...columns, colActive] : batchColumns}
        expandable={
          tabKey !== APWorkflowStatusTab.EXPORTED
            ? undefined
            : {
                expandedRowRender: (item) => {
                  const sorter = childSorters[item.id];
                  let sortedData = item?.espWorkflows || [];
                  if (sorter && sorter.field && sorter.order) {
                    sortedData = [...sortedData].sort((a, b) => {
                      const aValue = a[sorter.field];
                      const bValue = b[sorter.field];
                      if (typeof aValue === "string" && typeof bValue === "string") {
                        const result = aValue.localeCompare(bValue);
                        return sorter.order === "ascend" ? result : -result;
                      }
                      if (typeof aValue === "number" && typeof bValue === "number") {
                        const result = aValue - bValue;
                        return sorter.order === "ascend" ? result : -result;
                      }
                      return 0;
                    });
                  }

                  return (
                    <Table
                      rowKey="id"
                      columns={[...columns, colActive]}
                      dataSource={sortedData}
                      pagination={false}
                      bordered
                      size="small"
                      className="table-fm"
                      rowClassName={(_, index) => (index % 2 === 0 ? "highlight-row" : "")}
                      scroll={{ x: 1200 }}
                      loading={loading || loadingTableView}
                      onChange={(_, __, sorter) => {
                        setChildSorters((prev) => ({
                          ...prev,
                          [item.id]: sorter,
                        }));
                      }}
                    />
                  );
                },
              }
        }
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
        size="small"
        className="table-fm"
        rowClassName={(_, index) => {
          if (_?.isGrouped) {
            return _?.isParent ? "highlight-parent-row" : "highlight-group-row";
          }
          return index % 2 === 0 ? "highlight-row" : "";
        }}
        scroll={{ x: 1200 }}
      />

      {showModal && (
        <CustomizeTableModal
          open={showModal}
          onClose={() => setShowModal(false)}
          configsValue={columnConfigs}
          onSave={handleSaveCustomizeTable}
          tableName={TABLE_NAME}
          isSave={isDefaultView}
        />
      )}
      {openFilter && (
        <FilterDrawer
          title={`Filter ${type === WorkflowTypeEnum.TourEvent ? `Tour Event` : `Other`} Workflow`}
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          onApply={handleApplyFilters}
          values={filters}
          children={<FilterTourEventOrOtherWorkflow masterData={masterData} />}
        />
      )}
      {showModalCreateBatch && (
        <ModalCreateBatch
          open={showModalCreateBatch}
          onCancel={() => {
            setShowModalCreateBatch(false);
          }}
          onCreate={handleCreateBatch}
        />
      )}
      {modalConfirm?.isOpen && (
        <ModalConfirm
          title={modalConfirm.title}
          description={modalConfirm.description}
          open={modalConfirm.isOpen}
          onCancel={() => setModalConfirm(null)}
          onOk={modalConfirm.onConfirm}
        />
      )}
    </>
  );
};

export default TourEventOrOtherWorkflowTable;
